import Images from '@/assets/Images';
import {
  AccountCircle as AccountCircleIcon,
  Business as BusinessIcon,
  Logout as LogoutIcon,
  Menu as MenuIcon,
  Person as PersonIcon,
} from '@mui/icons-material';
import {
  AppBar,
  Avatar,
  Box,
  IconButton,
  Menu,
  MenuItem,
  Toolbar,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import useAuth from '../../../hooks/useAuth';
import CompanyBadge from '../../common/CompanyBadge';
import CompanySwitcher from '../../common/CompanySwitcher';
import NotificationCenter from '../../notifications/NotificationCenter';
import styles from './header.module.scss';
import { useLocation, useNavigate } from 'react-router-dom';
import { USER_GUIDE_ROUTES } from '../../../utils/constants';
import { useGetAuth0MeQuery } from '@/store/api/userApi';
import { getDocumentUrl } from '@/services/documentService';
import companyLogo from '../../../assets/images/logo/GWCLogo.svg';

interface HeaderProps {
  drawerWidth: number;
  onSidebarToggle: () => void;
  showSidebar?: boolean;
}

const Header: React.FC<HeaderProps> = ({
  drawerWidth,
  onSidebarToggle,
  showSidebar = true,
}) => {
  const { user, logout } = useAuth();
  const theme = useTheme();
  const { data: auth0MeData, isLoading: auth0Loading } = useGetAuth0MeQuery();

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const navigate = useNavigate();
  const location = useLocation();

  // Logo state management
  const [apiLogo, setApiLogo] = useState<string | null>(null);
  const [logoSrc, setLogoSrc] = useState<string | null>(null);

  // Fetch logo URL from API
  useEffect(() => {
    let cancelled = false;

    const fetchLogo = async () => {
      const logoId =
        auth0MeData?.user?.company_details?.profile?.logo ??
        auth0MeData?.user?.company_details?.profile?.logo_url;

      if (!logoId) {
        if (!cancelled) setApiLogo(null);
        return;
      }

      try {
        const url = await getDocumentUrl(logoId);
        if (!cancelled) {
          setApiLogo(url || null);
        }
      } catch (err) {
        console.error('Logo fetch failed:', err);
        if (!cancelled) setApiLogo(null);
      }
    };

    fetchLogo();

    return () => {
      cancelled = true;
    };
  }, [auth0MeData]);

  // Preload utility
  const preload = (src: string) =>
    new Promise<boolean>((resolve) => {
      const img = new Image();
      img.onload = () => resolve(true);
      img.onerror = () => resolve(false);
      img.src = src;
    });

  // Decide final logo after preload
  useEffect(() => {
    let canceled = false;
    
    if (auth0Loading) return;

    const decideLogo = async () => {
      if (apiLogo) {
        const ok = await preload(apiLogo);
        if (!canceled) {
          setLogoSrc(ok ? apiLogo : Images.LOGO);
        }
      } else {
        const logoId =
          auth0MeData?.user?.company_details?.profile?.logo ??
          auth0MeData?.user?.company_details?.profile?.logo_url;

        if (logoId && !apiLogo) {
          return;
        }

        if (!canceled) {
          setLogoSrc(Images.LOGO);
        }
      }
    };

    decideLogo();
    
    return () => {
      canceled = true;
    };
  }, [apiLogo, auth0Loading, auth0MeData]);

  // Cleanup
  useEffect(() => {
    return () => {
      if (apiLogo) {
        URL.revokeObjectURL(apiLogo);
      }
    };
  }, [apiLogo]);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    handleClose();
    // Do NOT clear localStorage/sessionStorage here: the OIDC user (with its
    // id_token) is stored in localStorage, and signoutRedirect() needs it to
    // attach id_token_hint to the end-session request. Clearing it first strips
    // the hint, so Authentik can't end the SSO session or redirect back and
    // strands the user on its "Not Found" session-end page. The provider's
    // logout() already handles token/session teardown itself.
    logout();
  };

  const buildHelpUrl = (pathname: string): string => {
    const baseUrl = import.meta.env.VITE_USER_GUIDE_URL ?? '';
    const entry = USER_GUIDE_ROUTES.find(({ match }) => {
      const patterns = Array.isArray(match) ? match : [match];
      return patterns.some(
        (p) => pathname === p || pathname.startsWith(`${p}/`),
      );
    });
    return entry ? `${baseUrl.replace(/\/$/, '')}${entry.path}` : baseUrl;
  };
  const helpUrl = buildHelpUrl(location.pathname);

          return (
    <AppBar
      position="fixed"
      sx={{
        boxShadow: 1,
        bgcolor: theme.palette.primary.main,
        color: 'white',
        borderBottom: 'none',
        zIndex: (theme) => theme.zIndex.drawer + 1,
      }}
    >
      <Toolbar sx={{ bgcolor: theme.palette.primary.main, paddingLeft: '0 !important' }}>
        {showSidebar && (
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={onSidebarToggle}
            sx={{ mr: 2, display: { md: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
        )}

                {/* Logo section - left side */}
        <Box className={styles.appLogo}>
          {logoSrc ? (
            <img
              src={logoSrc}
              alt="Company Logo"
              width={100}
              style={{
                objectFit: 'contain',
                maxHeight: '50px',
              }}
              onError={(e) => {
                const target = e.target as HTMLImageElement;
                if (target.src !== Images.LOGO) {
                  target.src = Images.LOGO;
                }
              }}
            />
          ) : (
            <img
              src={companyLogo}
              alt="Company Logo"
              width={100}
              style={{
                objectFit: 'contain',
                maxHeight: '50px',
              }}
            />
          )}
        </Box>

        {/* Spacer to push navigation to the right */}
        <Box sx={{ flexGrow: 1 }} />

        {/* Navigation Menu on Right */}
        <Box sx={{ display: 'flex', alignItems: 'center', }}>
          <ul className={styles.headerNavMenu} style={{ marginBottom: 0 }}>
            {/* Company Switcher */}
            {/* <li>
              <CompanySwitcher size="small" />
            </li> */}
            <li>
              <Tooltip title="Help">
                <a
                  className={styles.help}
                  href={helpUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <img src={Images.HELPGRAY} alt="Help" width="18" />
                </a>
              </Tooltip>
            </li>
            {/* <li className={styles.notification}>
              <NotificationCenter />
            </li> */}
            <li>
              <Tooltip title="Account">
                <IconButton
                  onClick={handleClick}
                  size="large"
                  aria-controls={open ? 'account-menu' : undefined}
                  aria-haspopup="true"
                  aria-expanded={open ? 'true' : undefined}
                  color="inherit"
                >
                  {user?.firstName ? (
                    <Avatar
                      sx={{ width: 32, height: 32, bgcolor: 'primary.main' }}
                    >
                      {`${user.firstName.charAt(0)}${user.lastName?.charAt(0) || ''}`}
                    </Avatar>
                  ) : (
                    <AccountCircleIcon />
                  )}
                </IconButton>
              </Tooltip>
            </li>
          </ul>
        </Box>

        <Menu
          anchorEl={anchorEl}
          id="account-menu"
          open={open}
          onClose={handleClose}
          onClick={handleClose}
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        >
          {/* Display current company in menu */}
          <MenuItem disabled>
            <BusinessIcon fontSize="small" sx={{ mr: 1 }} />
            <CompanyBadge showIcon={false} />
          </MenuItem>
          <MenuItem
            onClick={() => {
              handleClose(); // Closes the menu
              navigate('/profile'); // Navigates to /profile
            }}
          >
            <PersonIcon fontSize="small" sx={{ mr: 1 }} />
            {user?.name}
          </MenuItem>
          <MenuItem onClick={handleLogout}>
            <LogoutIcon fontSize="small" sx={{ mr: 1 }} />
            Logout
          </MenuItem>
        </Menu>
      </Toolbar>
    </AppBar>
  );
};

export default Header;
